// src/pages/CierreCajaDiario.js
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Grid, Button,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Paper, CircularProgress, Chip, FormControl, InputLabel, Select, MenuItem,
  IconButton, Tooltip, Stack
} from '@mui/material';
import {
  NavigateBefore as PrevIcon,
  NavigateNext as NextIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/es';
import api from '../api/api';

// Helper para obtener la sucursal del token (igual que en AlbaranesGestion)
const obtenerSucursalToken = () => {
  const token = localStorage.getItem('token');
  if (token) {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]));
      const sucursalId = payload.id_sucursal || payload.sucursal_id || payload.idSucursal || null;
      return sucursalId ? String(sucursalId) : '';
    } catch (err) {
      console.error('Error al leer sucursal del token:', err);
    }
  }
  return '';
};

const formatCurrency = (val) =>
  new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' }).format(val || 0);

const CierreCajaDiario = () => {
  // Estados principales
  const [fecha, setFecha] = useState(dayjs());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Sucursal
  const [sucursalUsuario] = useState(obtenerSucursalToken);
  const [sucursalFiltro, setSucursalFiltro] = useState(obtenerSucursalToken);
  const [sucursales, setSucursales] = useState([]);

  // Cargar catálogo de sucursales
  useEffect(() => {
    const fetchSucursales = async () => {
      try {
        const res = await api.get('/sucursales/');
        setSucursales(res.data || []);
      } catch (err) {
        console.error('Error al cargar sucursales:', err);
      }
    };
    fetchSucursales();
  }, []);

  // CierreCajaDiario.js

const fetchCierre = useCallback(async () => {
  // 1. Si la fecha no es un objeto Dayjs válido o no está completa, limpia los datos y no llames al backend
  if (!fecha || !dayjs.isDayjs(fecha) || !fecha.isValid()) {
    setData(null);
    return;
  }

  setLoading(true);
  setError(null);

  try {
    const params = {
      fecha: fecha.format('YYYY-MM-DD'),
    };
    if (sucursalFiltro) {
      params.id_sucursal = sucursalFiltro;
    }

    const res = await api.get('/documentos_fiscales/cierre-caja', { params });
    setData(res.data);
  } catch (err) {
    console.error('Error cargando cierre de caja:', err);

    const detail = err.response?.data?.detail;
    let mensaje = 'Error al cargar el cierre de caja';

    // 2. Extraer el texto de forma 100% inmune a objetos de React
    try {
      if (typeof detail === 'string') {
        mensaje = detail;
      } else if (Array.isArray(detail)) {
        mensaje = detail
          .map((e) => {
            if (typeof e === 'string') return e;
            if (e && typeof e === 'object') {
              // Si msg es un string lo usa, de lo contrario convierte todo el objeto a JSON string
              return typeof e.msg === 'string' ? e.msg : JSON.stringify(e);
            }
            return String(e);
          })
          .join(' | ');
      } else if (detail && typeof detail === 'object') {
        mensaje = typeof detail.msg === 'string' ? detail.msg : JSON.stringify(detail);
      } else if (err.message) {
        mensaje = String(err.message);
      }
    } catch (parseError) {
      mensaje = 'Ocurrió un error inesperado al procesar la respuesta.';
    }

    // Doble verificación: Garantizar que la variable final sea estrictamente un string
    setError(typeof mensaje === 'string' ? mensaje : JSON.stringify(mensaje));
    setData(null);
  } finally {
    setLoading(false);
  }
}, [fecha, sucursalFiltro]);

  useEffect(() => {
    fetchCierre();
  }, [fetchCierre]);

  // Navegación de fechas
  const irDiaAnterior = () => setFecha((prev) => prev.subtract(1, 'day'));
  const irDiaSiguiente = () => setFecha((prev) => prev.add(1, 'day'));
  const irHoy = () => setFecha(dayjs());

  // Exportar Excel
  const handleExportarExcel = async () => {
    try {
      const params = {
        fecha: fecha.format('YYYY-MM-DD'),
      };
      if (sucursalFiltro) {
        params.id_sucursal = sucursalFiltro;
      }

      const res = await api.get('/documentos_fiscales/cierre-caja/excel', {
        params,
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `Cierre_Diario_${fecha.format('YYYY-MM-DD')}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error exportando Excel:', err);
      alert('No se pudo generar el archivo Excel');
    }
  };

  const resumen = data?.resumen;
  const porForma = resumen?.por_forma_pago || {};

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="es">
      <Box sx={{ p: 3 }}>
        {/* Título y acciones */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, flexWrap: 'wrap', gap: 2 }}>
          <Typography variant="h5" fontWeight="bold">
            📊 Cierre de Caja Diario
          </Typography>

          <Stack direction="row" spacing={1}>
            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={fetchCierre}
              disabled={loading}
            >
              Actualizar
            </Button>
            <Button
              variant="contained"
              color="success"
              startIcon={<DownloadIcon />}
              onClick={handleExportarExcel}
              disabled={loading || !data || data.resumen?.total_dtes === 0}
            >
              Exportar Excel
            </Button>
          </Stack>
        </Box>

        {/* Filtros */}
        <Paper sx={{ p: 2, mb: 3 }} elevation={0} variant="outlined">
          <Grid container spacing={2} alignItems="center">
            {/* Selector de fecha + navegación */}
            <Grid item xs={12} md={6}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Tooltip title="Día anterior">
                  <IconButton onClick={irDiaAnterior} size="small">
                    <PrevIcon />
                  </IconButton>
                </Tooltip>

              <DatePicker
                label="Fecha de cierre"
                value={fecha}
                onChange={(newValue) => setFecha(newValue)}
                format="DD/MM/YYYY"
                slotProps={{
                  textField: { 
                    size: 'small', 
                    fullWidth: true,
                    // Evita que MUI renderice objetos de error en el helperText del input
                    error: Boolean(fecha && dayjs.isDayjs(fecha) && !fecha.isValid())
                  },
                }}
              />

                <Tooltip title="Día siguiente">
                  <IconButton onClick={irDiaSiguiente} size="small">
                    <NextIcon />
                  </IconButton>
                </Tooltip>

                <Button size="small" onClick={irHoy} variant="text">
                  Hoy
                </Button>
              </Stack>
            </Grid>

            {/* Filtro de sucursal */}
            <Grid item xs={12} md={4}>
              <FormControl size="small" fullWidth>
                <InputLabel>Sucursal</InputLabel>
                <Select
                  value={sucursalFiltro}
                  label="Sucursal"
                  onChange={(e) => setSucursalFiltro(e.target.value)}
                >
                  <MenuItem value="">
                    <em>Todas las Sucursales</em>
                  </MenuItem>
                  {sucursales.map((s) => (
                    <MenuItem key={s.id_sucursal} value={String(s.id_sucursal)}>
                      {s.nombre_sucursal || s.nombre}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {sucursalFiltro && sucursalUsuario && sucursalFiltro === sucursalUsuario && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  (tu sucursal por defecto)
                </Typography>
              )}
            </Grid>
          </Grid>
        </Paper>

        {/* Loading */}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Error */}
        {error && !loading && (
          <Paper sx={{ p: 3, textAlign: 'center', bgcolor: 'error.light', color: 'error.contrastText' }}>
            <Typography>{error}</Typography>
          </Paper>
        )}

        {/* Contenido */}
        {!loading && !error && data && (
          <>
            {/* Cards de Resumen */}
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={12} sm={6} md={2.4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      DTEs emitidos
                    </Typography>
                    <Typography variant="h4" fontWeight="bold" color="primary">
                      {resumen.total_dtes}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Monto Total
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="success.main">
                      {formatCurrency(resumen.monto_total)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      IVA
                    </Typography>
                    <Typography variant="h5" fontWeight="bold">
                      {formatCurrency(resumen.iva)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Transferencias
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="info.main">
                      {formatCurrency(porForma.transferencias)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} sm={6} md={2.4}>
                <Card variant="outlined">
                  <CardContent sx={{ textAlign: 'center', py: 2 }}>
                    <Typography variant="caption" color="text.secondary">
                      Efectivo
                    </Typography>
                    <Typography variant="h5" fontWeight="bold" color="warning.main">
                      {formatCurrency(porForma.efectivo)}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Desglose por forma de pago */}
            {data.formas_pago?.length > 0 && (
              <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Cierre por Forma de Pago (CAT-017)
                  </Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'grey.100' }}>
                          <TableCell><strong>Forma de Pago</strong></TableCell>
                          <TableCell align="center"><strong>Transacciones</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.formas_pago.map((fp) => (
                          <TableRow key={fp.codigo}>
                            <TableCell>{fp.nombre}</TableCell>
                            <TableCell align="center">{fp.transacciones}</TableCell>
                            <TableCell align="right">{formatCurrency(fp.total)}</TableCell>
                          </TableRow>
                        ))}
                        <TableRow sx={{ bgcolor: 'success.50' }}>
                          <TableCell><strong>TOTAL GENERAL</strong></TableCell>
                          <TableCell align="center">
                            <strong>
                              {data.formas_pago.reduce((acc, fp) => acc + fp.transacciones, 0)}
                            </strong>
                          </TableCell>
                          <TableCell align="right">
                            <strong>{formatCurrency(resumen.monto_total)}</strong>
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            )}

            {/* Tabla de detalle */}
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detalle de DTEs del día
                </Typography>

                {data.detalle.length === 0 ? (
                  <Box sx={{ py: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                      No hay DTEs registrados para esta fecha.
                    </Typography>
                  </Box>
                ) : (
                  <TableContainer component={Paper} elevation={0}>
                    <Table size="small" sx={{ minWidth: 1000 }}>
                      <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                          <TableCell><strong>N° Control</strong></TableCell>
                          <TableCell><strong>Tipo</strong></TableCell>
                          <TableCell><strong>Cliente / Receptor</strong></TableCell>
                          <TableCell><strong>Condición</strong></TableCell>
                          <TableCell align="right"><strong>Subtotal</strong></TableCell>
                          <TableCell align="right"><strong>IVA</strong></TableCell>
                          <TableCell align="right"><strong>Total</strong></TableCell>
                          <TableCell><strong>Forma(s) de Pago</strong></TableCell>
                          <TableCell><strong>Estado</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {data.detalle.map((item) => (
                          <TableRow
                            key={item.id}
                            sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}
                          >
                            <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                              {item.numero_control}
                            </TableCell>
                            <TableCell>{item.tipo_nombre}</TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>{item.receptor}</TableCell>
                            <TableCell>{item.condicion}</TableCell>
                            <TableCell align="right">{formatCurrency(item.subtotal)}</TableCell>
                            <TableCell align="right">{formatCurrency(item.iva)}</TableCell>
                            <TableCell align="right">
                              <Chip
                                label={formatCurrency(item.total_pagar)}
                                size="small"
                                color="success"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell sx={{ fontSize: '0.8rem', maxWidth: 200 }}>
                              {item.formas_pago}
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={item.estado_vinculacion}
                                size="small"
                                color={
                                  item.estado_vinculacion?.includes('VINCULADO')
                                    ? 'success'
                                    : 'default'
                                }
                                variant="outlined"
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </CardContent>
            </Card>
          </>
        )}
      </Box>
    </LocalizationProvider>
  );
};

export default CierreCajaDiario;